// Composite posing shared by the preview sheet, the hangar portrait and the
// sandbox: places the separate arm parts and the held weapon from a frame's
// MechFrameMeta exactly the way MechView does at runtime (see ARCHITECTURE
// "MechView layering"): aimWeight 1 → the front fist holds the weapon on the
// aim line and the rear arm reaches the foregrip (two-bone IK, elbows down);
// aimWeight 0 → the arms follow the authored armF/armR poses and the weapon
// follows the front forearm. Shoot frames add recoil (pull-back along the aim)
// and lift (muzzle rise).

import type { ArmPose, MechFrameMeta, WeaponArt } from '../../core/types';
import { canvas, ctx2d, multiplied, tinted } from './draw';
import type { Sprite } from './sprite';

export interface ArmSolve {
  upper: number;
  fore: number;
  sx: number;
  sy: number;
  ex: number;
  ey: number;
  hx: number;
  hy: number;
}

export interface PoseResult {
  front: ArmSolve;
  rear: ArmSolve;
  /** Weapon grip position and rotation (cell px, radians). */
  weapon: { x: number; y: number; rot: number };
}

/** Front-hand reach along the aim, as a fraction of the full arm length. */
const AIM_REACH = 0.66;
/** The grip sits this far below the shoulder→aim line (px). */
const GRIP_DROP = 3;

/** The support arm reaches its foregrip at no more than this share of its full length. */
export const SUPPORT_STRETCH = 0.94;

/**
 * Largest front-hand distance along the aim (from the front shoulder) that still lets the
 * rear hand reach the foregrip within `reach`. (dx, dy) = front shoulder − rear shoulder,
 * (ac, as) = aim direction, drop = hand offset below the aim line, (fx, fy) = foregrip
 * offset from the grip in the weapon frame. Shared by the composites and MechView.
 */
export function supportReachLimit(dx: number, dy: number, ac: number, as: number, drop: number, fx: number, fy: number, reach: number): number {
  // Rear hand = shoulderF + u·(hd + fx) + n·(drop + fy), u = aim, n = its normal; solve |rear hand − shoulderR| = reach.
  const ox = dx - as * (drop + fy);
  const oy = dy + ac * (drop + fy);
  const b = ox * ac + oy * as;
  const disc = b * b - (ox * ox + oy * oy) + reach * reach;
  if (disc < 0) return 0;
  return -b + Math.sqrt(disc) - fx;
}

const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
const lerpAngle = (a: number, b: number, t: number): number => a + wrap(b - a) * t;

function ik(sx: number, sy: number, tx: number, ty: number, l1: number, l2: number): ArmSolve {
  const dx = tx - sx;
  const dy = ty - sy;
  const dist = Math.hypot(dx, dy);
  const base = dist > 1e-6 ? Math.atan2(dy, dx) : 0;
  const d = Math.min(l1 + l2 - 1e-3, Math.max(Math.abs(l1 - l2) + 1e-3, dist));
  const cosA = (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d);
  const upper = base + Math.acos(Math.max(-1, Math.min(1, cosA)));
  const ex = sx + Math.cos(upper) * l1;
  const ey = sy + Math.sin(upper) * l1;
  const fore = Math.atan2(sy + Math.sin(base) * d - ey, sx + Math.cos(base) * d - ex);
  return fk(sx, sy, upper, fore, l1, l2);
}

function fk(sx: number, sy: number, upper: number, fore: number, l1: number, l2: number): ArmSolve {
  const ex = sx + Math.cos(upper) * l1;
  const ey = sy + Math.sin(upper) * l1;
  return { upper, fore, sx, sy, ex, ey, hx: ex + Math.cos(fore) * l2, hy: ey + Math.sin(fore) * l2 };
}

function blend(pose: ArmPose, aimed: ArmSolve, w: number, l1: number, l2: number): ArmSolve {
  if (w >= 0.999) return aimed;
  return fk(aimed.sx, aimed.sy, lerpAngle(pose.upper, aimed.upper, w), lerpAngle(pose.fore, aimed.fore, w), l1, l2);
}

/**
 * Solve both arms and the weapon for one frame. `aim` is the local
 * (right-facing) aim angle; `kick` scales the shoot-sheet recoil/lift (0..1).
 */
export function poseArms(
  meta: MechFrameMeta,
  aim: number,
  upperLen: number,
  foreLen: number,
  weapon: Pick<WeaponArt, 'foregrip' | 'holdReach' | 'holdDrop'> | null,
  kick = 1,
): PoseResult {
  const sF = meta.attach.shoulderF;
  const sR = meta.attach.shoulderR;
  const lift = (meta.lift ?? 0) * kick;
  const recoil = (meta.recoil ?? 0) * kick;
  const a = aim + lift;
  const c = Math.cos(aim);
  const s = Math.sin(aim);
  const drop = weapon?.holdDrop ?? GRIP_DROP;
  const fg0 = weapon?.foregrip ?? { x: 12, y: 0 };
  // Never hold the gun further out than the support hand can still reach its foregrip.
  const fit = supportReachLimit(sF.x - sR.x, sF.y - sR.y, c, s, drop, fg0.x, fg0.y, (upperLen + foreLen) * SUPPORT_STRETCH);
  const reach = Math.max(Math.abs(upperLen - foreLen) + 2, Math.min((upperLen + foreLen) * (weapon?.holdReach ?? AIM_REACH), fit) - recoil);
  const tx = sF.x + c * reach - s * drop;
  const ty = sF.y + s * reach + c * drop;
  const frontAim = ik(sF.x, sF.y, tx, ty, upperLen, foreLen);
  const front = blend(meta.armF, frontAim, meta.aimWeight, upperLen, foreLen);
  const rot = lerpAngle(front.fore, a, meta.aimWeight);
  const fg = weapon?.foregrip ?? { x: 12, y: 0 };
  const fx = front.hx + Math.cos(rot) * fg.x - Math.sin(rot) * fg.y;
  const fy = front.hy + Math.sin(rot) * fg.x + Math.cos(rot) * fg.y;
  const rearAim = ik(sR.x, sR.y, fx, fy, upperLen, foreLen);
  const rear = blend(meta.armR, rearAim, meta.aimWeight, upperLen, foreLen);
  return { front, rear, weapon: { x: front.hx, y: front.hy, rot } };
}

export interface CompositeParts {
  upper: Sprite;
  fore: Sprite;
  upperLen: number;
  foreLen: number;
  rearTint: number;
  reactor: number;
  weapon: { sprite: Sprite; art: WeaponArt } | null;
}

/** Cached tinted variants (rear arm multiply tint, white glow → colour). */
export class CompositeCache {
  private rear = new Map<HTMLCanvasElement, HTMLCanvasElement>();
  private tint = new Map<string, HTMLCanvasElement>();
  private idx = new Map<HTMLCanvasElement, number>();

  rearOf(c: HTMLCanvasElement, color: number): HTMLCanvasElement {
    let r = this.rear.get(c);
    if (!r) {
      r = multiplied(c, color);
      this.rear.set(c, r);
    }
    return r;
  }

  tintOf(c: HTMLCanvasElement, color: number): HTMLCanvasElement {
    let id = this.idx.get(c);
    if (id === undefined) {
      id = this.idx.size;
      this.idx.set(c, id);
    }
    const key = `${id}:${color}`;
    let r = this.tint.get(key);
    if (!r) {
      r = tinted(c, color);
      this.tint.set(key, r);
    }
    return r;
  }
}

function drawPart(g: CanvasRenderingContext2D, img: HTMLCanvasElement, sp: Sprite, x: number, y: number, rot: number, S: number, add = false): void {
  g.save();
  if (add) g.globalCompositeOperation = 'lighter';
  g.translate(x * S, y * S);
  g.rotate(rot);
  g.drawImage(img, -sp.ox, -sp.oy);
  g.restore();
}

/**
 * Composite one frame (rear arm → body → body glow → weapon → front arm) into
 * `g` at (dx, dy). `body`/`glow` are cell images at the render scale S.
 */
export function compositeFrame(
  g: CanvasRenderingContext2D,
  dx: number,
  dy: number,
  S: number,
  body: CanvasImageSource,
  glow: HTMLCanvasElement | null,
  meta: MechFrameMeta,
  parts: CompositeParts,
  cache: CompositeCache,
  aim = 0.06,
): void {
  const pose = poseArms(meta, aim, parts.upperLen, parts.foreLen, parts.weapon?.art ?? null);
  g.save();
  g.translate(dx, dy);
  const arm = (sol: ArmSolve, rear: boolean): void => {
    const up = rear ? cache.rearOf(parts.upper.body, parts.rearTint) : parts.upper.body;
    const fo = rear ? cache.rearOf(parts.fore.body, parts.rearTint) : parts.fore.body;
    drawPart(g, up, parts.upper, sol.sx, sol.sy, sol.upper, S);
    if (parts.upper.glow) drawPart(g, cache.tintOf(parts.upper.glow, parts.reactor), parts.upper, sol.sx, sol.sy, sol.upper, S, true);
    drawPart(g, fo, parts.fore, sol.ex, sol.ey, sol.fore, S);
    if (parts.fore.glow) drawPart(g, cache.tintOf(parts.fore.glow, parts.reactor), parts.fore, sol.ex, sol.ey, sol.fore, S, true);
  };
  arm(pose.rear, true);
  g.drawImage(body, 0, 0);
  if (glow) {
    g.save();
    g.globalCompositeOperation = 'lighter';
    // Not cached: callers may reuse one scratch canvas for every frame.
    g.drawImage(tinted(glow, parts.reactor), 0, 0);
    g.restore();
  }
  // The weapon sits between the arms: the near (front) arm and its fist wrap over the grip.
  if (parts.weapon) {
    const { sprite, art } = parts.weapon;
    drawPart(g, sprite.body, sprite, pose.weapon.x, pose.weapon.y, pose.weapon.rot, S);
    if (sprite.glow) drawPart(g, cache.tintOf(sprite.glow, art.glowColor ?? 0xffffff), sprite, pose.weapon.x, pose.weapon.y, pose.weapon.rot, S, true);
  }
  arm(pose.front, false);
  g.restore();
}

/** Crop helper: copy a cell out of a sheet canvas (for compositing from sheets). */
export function cellOf(sheet: HTMLCanvasElement, x: number, y: number, size: number): HTMLCanvasElement {
  const c = canvas(size, size);
  ctx2d(c).drawImage(sheet, x, y, size, size, 0, 0, size, size);
  return c;
}
